import { LogOut, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { BranchSelector } from './BranchSelector';
import { InactivityLock } from './InactivityLock';
import { useLogout } from '../auth/useAuth';
import { NotificationsBell } from './notifications/NotificationsBell';
import { Sidebar } from './Sidebar';
import { ThemeToggle } from './ThemeToggle';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { SplashScreen } from '@/shared/ui/SplashScreen';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

const ROLE_IDLE_MS: Record<string, number> = {
  cashier: 10 * 60_000,
  administrator: 15 * 60_000,
  veterinarian: 20 * 60_000,
  assistant: 15 * 60_000,
  info_panel: 23 * 60 * 60_000,
  info_panel_2: 23 * 60 * 60_000,
};
const DEFAULT_IDLE_MS = 15 * 60_000;
const PANEL_ROLES = ['info_panel', 'info_panel_2'] as const;

export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const workstation = useWorkstationStore();
  const logout = useLogout();
  const is_waiting_room_view = location.pathname === '/app/display/waiting-room';
  const is_medical_queue_view = location.pathname === '/app/display/medical-queue';
  const [is_display_sidebar_open, set_is_display_sidebar_open] = useState(false);

  if (!user) return <SplashScreen />;

  const idle_ms =
    user.roles.reduce<number | null>((acc, r) => {
      const candidate = ROLE_IDLE_MS[r];
      if (candidate === undefined) return acc;
      return acc === null ? candidate : Math.min(acc, candidate);
    }, null) ?? DEFAULT_IDLE_MS;
  const is_info_panel_only =
    user.roles.length === 1 && PANEL_ROLES.some((role) => role === user.roles[0]);
  const is_panel_display_view =
    is_waiting_room_view || (is_medical_queue_view && is_info_panel_only);

  const onClickLogout = async (): Promise<void> => {
    await logout();
  };

  useEffect(() => {
    if (is_panel_display_view) {
      set_is_display_sidebar_open(false);
      return;
    }
    set_is_display_sidebar_open(true);
  }, [is_panel_display_view]);

  const should_show_sidebar = !is_panel_display_view || is_display_sidebar_open;

  return (
    <div className="flex h-full">
      {should_show_sidebar ? <Sidebar user={user} /> : null}

      <div className="relative flex flex-1 flex-col">
        {is_panel_display_view ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="fixed left-3 top-3 z-50 h-9 w-9 rounded-full border-[#d7e5f3] bg-white/75 text-[#07195a] shadow-sm backdrop-blur"
            onClick={() => {
              set_is_display_sidebar_open((prev) => !prev);
            }}
            aria-label={is_display_sidebar_open ? t('layout.hide_menu') : t('layout.show_menu')}
            title={is_display_sidebar_open ? t('layout.hide_menu') : t('layout.show_menu')}
          >
            {is_display_sidebar_open ? (
              <X className="h-4 w-4" aria-hidden />
            ) : (
              <Menu className="h-4 w-4" aria-hidden />
            )}
          </Button>
        ) : null}

        <header
          className={cn(
            'border-border bg-card/50 flex items-center justify-between border-b px-6 py-3',
            is_panel_display_view ? 'hidden' : '',
          )}
        >
          <div className="flex items-center gap-3">
            {is_panel_display_view ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  set_is_display_sidebar_open((prev) => !prev);
                }}
              >
                {is_display_sidebar_open ? (
                  <X className="h-4 w-4" aria-hidden />
                ) : (
                  <Menu className="h-4 w-4" aria-hidden />
                )}
                <span>
                  {is_display_sidebar_open ? t('layout.hide_menu') : t('layout.show_menu')}
                </span>
              </Button>
            ) : null}
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wider">
                {workstation.display_name ?? t('workstation.unconfigured')}
              </p>
              <h1 className="text-base font-semibold">{user.full_name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BranchSelector />
            <NotificationsBell />
            <ThemeToggle />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickLogout();
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">{t('auth.sign_out')}</span>
            </Button>
          </div>
        </header>

        <main className={cn('flex-1 overflow-auto', is_panel_display_view ? 'p-0' : 'p-6')}>
          <Outlet />
        </main>
      </div>

      {!is_info_panel_only ? <InactivityLock idle_ms={idle_ms} /> : null}
    </div>
  );
}
